import * as geolib from 'geolib';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setBookingDetails, clearBookingForm } from '../../lib/store/bookingSlice';
import { sendBookingEmail } from '../api/api';
import { hashEmail } from '../helpers/hashUtils';
import { clearBookingState } from '../helpers/bookingPersistence';
import {
    attemptRawDataRecovery,
    validateBookingFormData,
    validateCompleteRawData,
    validateRawForm1Data,
    validateRawForm2Data
} from '../helpers/rawDataIntegrity';

export const useBookingForm = () => {
    const router = useRouter();
    const dispatch = useDispatch();
    const bookingData = useSelector(state => state.booking);
    
    const [errors, setErrors] = useState({});
    const [isClickedSubmit, setIsClickedSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '+44',
        smallSuitCases: '0',
        largeSuitCases: '0',
        reason: '',
        details: '',
        isChecked: false,
    });

    // CRITICAL: Recovery mechanism to restore rawForm1Data if missing
    useEffect(() => {
        // Check if rawForm1Data is missing from Redux
        if (!bookingData.rawForm1Data || Object.keys(bookingData.rawForm1Data).length === 0) {
            console.warn('[RAW_DATA/RECOVERY] ⚠️ rawForm1Data missing from Redux, attempting recovery...');
            
            try {
                // Try primary recovery from sessionStorage
                const storedBookingData = sessionStorage.getItem('booking_form1');
                const backupRawData = sessionStorage.getItem('booking_raw_form1_backup');
                
                if (storedBookingData) {
                    const parsedData = JSON.parse(storedBookingData);
                    
                    // Restore full booking data to Redux
                    if (parsedData.rawForm1Data) {
                        dispatch(setBookingDetails(parsedData));
                    } else if (backupRawData) {
                        // Use backup if main storage doesn't have rawForm1Data
                        const parsedBackup = JSON.parse(backupRawData);
                        dispatch(setBookingDetails({
                            ...parsedData,
                            rawForm1Data: parsedBackup,
                        }));
                    }
                } else if (backupRawData) {
                    // Only backup exists
                    const parsedBackup = JSON.parse(backupRawData);
                    dispatch(setBookingDetails({
                        ...bookingData,
                        rawForm1Data: parsedBackup,
                    }));
                } else {
                    // Try emergency localStorage fallback
                    const emergencyData = localStorage.getItem('booking_raw_form1_emergency');
                    if (emergencyData) {
                        const parsedEmergency = JSON.parse(emergencyData);
                        dispatch(setBookingDetails({
                            ...bookingData,
                            rawForm1Data: parsedEmergency,
                        }));
                    } else {
                        console.error('[RAW_DATA/RECOVERY] ❌ CRITICAL: No raw data found in any storage!');
                    }
                }
            } catch (error) {
                console.error('[RAW_DATA/RECOVERY] ❌ Error during recovery:', error);
            }
        } 
    }, []); // Run once on mount


    // Function to calculate distance between pickup and dropoff locations
    const calculateDistance = () => {

        if (bookingData.pickupLat && bookingData.pickupLong && bookingData.dropOffLat && bookingData.dropOffLong) {
            try {
                const distance = geolib.getDistance(
                    {
                        lat: bookingData.pickupLat,
                        lng: bookingData.pickupLong,
                    },
                    {
                        lat: bookingData.dropOffLat,
                        lng: bookingData.dropOffLong,
                    }
                );
                
                // Convert from meters to kilometers, then to miles
                const distanceInKm = geolib.convertDistance(distance, 'km');
                const distanceInMiles = distanceInKm * 0.621371;

                
                return distanceInMiles;
            } catch (error) {
                console.error('❌ Error calculating distance:', error);
                return 0;
            }
        } else {
            console.log('⚠️ Missing location data for distance calculation');
            return 0;
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // First name validation
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Please enter first name';
        } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
            newErrors.firstName = 'First name can only contain letters and spaces';
        }

        // Last name validation
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Please enter last name';
        } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
            newErrors.lastName = 'Last name can only contain letters and spaces';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Please enter email address';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter valid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Please enter contact no';
        } else if (formData.phone.length < 9) {
            newErrors.phone = 'Contact No Must Have At Least 9 Digits';
        } else if (formData.phone.length > 12) {
            newErrors.phone = 'Contact No Cannot Exceed 12 Digits';
        }

        if (!formData.isChecked) {
            newErrors.isChecked = 'Please agree to the terms and conditions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsClickedSubmit(true);

        if (validateForm()) {
            try {
                setIsSubmitting(true);

                // First, validate processed Form 1 / trip data from Redux
                const bookingValidation = validateBookingFormData(bookingData);

                if (!bookingValidation.isValid) {
                    console.error('[TRIP_FORM/VALIDATION] ❌ Booking form data invalid:', bookingValidation);

                    const missingList = bookingValidation.missingFields.length
                        ? ` Missing fields: ${bookingValidation.missingFields.join(', ')}.`
                        : '';

                    alert(
                        `Some required fields in your trip form are missing or invalid. Please refill the trip details form.${missingList}`
                    );

                    setIsSubmitting(false);
                    await router.push('/');
                    return;
                }

                // CRITICAL: Pre-submission validation for raw data (Form 1)
                
                // Validate rawForm1Data using integrity checker
                const rawForm1Validation = validateRawForm1Data(bookingData.rawForm1Data);
                
                if (!rawForm1Validation.isValid) {
                    console.error('[RAW_DATA/VALIDATION] ❌ CRITICAL: rawForm1Data validation failed!');
                    console.error('[RAW_DATA/VALIDATION] Missing fields:', rawForm1Validation.missingFields);
                    console.error('[RAW_DATA/VALIDATION] Errors:', rawForm1Validation.errors);
                    
                    // Attempt emergency recovery using utility function
                    const recoveredData = attemptRawDataRecovery();
                    
                    if (recoveredData) {
                        // Validate recovered data
                        const recoveredValidation = validateRawForm1Data(recoveredData);
                        
                        if (recoveredValidation.isValid) {
                            console.warn('[RAW_DATA/VALIDATION] ⚠️ Recovered valid data - updating Redux...');
                            
                            // Update Redux with recovered data
                            dispatch(setBookingDetails({
                                ...bookingData,
                                rawForm1Data: recoveredData,
                            }));
                            
                            // Don't proceed - let user try again after recovery
                            alert('Your booking data was temporarily lost but has been recovered. Please click Submit again.');
                            setIsSubmitting(false);
                            return;
                        } else {
                            console.error('[RAW_DATA/VALIDATION] ❌ Recovered data is also invalid!');
                            console.error('[RAW_DATA/VALIDATION] Recovered validation:', recoveredValidation);
                        }
                    }
                    
                    // No recovery possible or recovered data invalid - block submission
                    console.error('[RAW_DATA/VALIDATION] ❌ FATAL: No valid backup data available!');
                    alert(`Critical error: Some required fields in your trip form are missing or corrupted: ${rawForm1Validation.missingFields.join(', ')}. Please refill the trip details form.`);
                    // Send user back to the trip form so they can refill it
                    setIsSubmitting(false);
                    await router.push('/');
                    setIsSubmitting(false);
                    return;
                }
                
                // Log any warnings (non-blocking)
                if (rawForm1Validation.errors.length > 0) {
                    console.warn('[RAW_DATA/VALIDATION] ⚠️ Warnings for rawForm1Data:', rawForm1Validation.errors);
                }
              
                // Get cookie consent from localStorage
                const cookieConsent = localStorage.getItem('cookie-consent') || 'not-set';

                // Create raw Form 2 data BEFORE any processing
                const rawForm2Data = {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    countryCode: formData.countryCode,
                    smallSuitCases: formData.smallSuitCases,
                    largeSuitCases: formData.largeSuitCases,
                    reason: formData.reason,
                    details: formData.details,
                    additionalDropoffs: formData.additionalDropoffs,
                    additionalPickups: formData.additionalPickups,
                    isChecked: formData.isChecked,
                    // Add timestamp to track when Form 2 was captured
                    capturedAt: new Date().toISOString(),
                };

                // Validate Form 2 raw data
                const rawForm2Validation = validateRawForm2Data(rawForm2Data);
                if (!rawForm2Validation.isValid) {
                    console.error('[RAW_DATA/VALIDATION] ❌ rawForm2Data validation failed!');
                    console.error('[RAW_DATA/VALIDATION] Missing fields:', rawForm2Validation.missingFields);
                    alert(`Form validation error: Missing required fields: ${rawForm2Validation.missingFields.join(', ')}`);
                    setIsSubmitting(false);
                    return;
                }

                // Combine raw data from both forms
                const completeRawData = {
                    // Raw Form 1 data (from Redux) - Now guaranteed to exist and validated
                    ...bookingData.rawForm1Data,
                    // Raw Form 2 data - Validated above
                    ...rawForm2Data,
                    // Browser and timestamp at the time of final submission
                    browserType: navigator.userAgent || 'Unknown',
                    submittedAt: new Date().toISOString(),
                    cookieConsent: cookieConsent,
                };
                
                // Validate complete raw data
                const completeValidation = validateCompleteRawData(completeRawData);
                if (!completeValidation.isValid) {
                    console.error('[RAW_DATA/VALIDATION] ❌ CRITICAL: Complete raw data validation failed!');
                    console.error('[RAW_DATA/VALIDATION] Errors:', completeValidation.errors);
                    alert('Critical error: Combined booking data is incomplete. Please contact support if this persists.');
                    setIsSubmitting(false);
                    return;
                }
                

                // CRITICAL: Save complete raw data to storage before API call (for audit/recovery)
                try {
                    sessionStorage.setItem('booking_complete_raw_data', JSON.stringify(completeRawData));
                    sessionStorage.setItem('booking_last_submission_attempt', new Date().toISOString());
                } catch (storageError) {
                    console.error('[RAW_DATA/BACKUP] ❌ Failed to backup complete raw data:', storageError);
                    // Continue anyway - don't block submission for storage errors
                }

                // Calculate distance
                const calculatedDistance = calculateDistance();
                
                const payload = {
                    email: formData.email?.trim() || '',
                    firstName: formData.firstName || '',
                    lastName: formData.lastName || '',
                    phoneNumber: formData.phone || '',
                    phonePreFix: formData.countryCode || '+44',
                    smallSuitCases: formData.smallSuitCases || '0',
                    largeSuitCases: formData.largeSuitCases || '0',
                    reason: formData.reason || '',
                    additionalInformation: formData.details || '',

                    // Booking Data (converted/processed)
                    departure: bookingData.departureUnix || 0,
                    departureString: (bookingData.departureUnix || 0).toString(),
                    destination: bookingData.dropOffAddress || '',
                    distance: calculatedDistance,
                    dropOffLat: bookingData.dropOffLat,
                    dropOffLong: bookingData.dropOffLong,

                    source: bookingData.pickUpAddress || '',
                    pickupLat: bookingData.pickupLat,
                    pickupLong: bookingData.pickupLong,

                    numberOfPassengers: parseInt(bookingData.noOfPassengers) || 1,

                    returnTime: bookingData.tripType === '2' ? bookingData.returnUnix || 0 : 0,
                    returnTimeString: bookingData.tripType === '2' ? (bookingData.returnUnix || 0).toString() : '0',

                    tripType: bookingData.tripType === '1' ? 0 : 1,
                    browserType: navigator.userAgent || 'Unknown',

                    googleAdId: bookingData.gclid || null,
                    leadSource: process.env.NEXT_PUBLIC_LEAD_SOURCE,
                    cookieConsent: cookieConsent,
                    
                    // Form source tracking
                    formSource: bookingData.formSource || 'unknown',
                    
                    // Complete raw data object from both forms (BEFORE any conversions)
                    rawData: completeRawData,
                    
                    // Additional fields for better tracking
                    timestamp: new Date().toISOString(),
                };

             
                // Validate payload before sending
                if (!payload.email || !payload.firstName || !payload.lastName || !payload.phoneNumber) {
                    console.error('❌ Required fields are missing in the payload');
                    throw new Error('Required fields are missing in the payload');
                }

                
                const response = await sendBookingEmail(payload);
                
               
                
                // Save successful submission record
                try {
                    const submissionRecord = {
                        timestamp: new Date().toISOString(),
                        email: payload.email,
                        success: true,
                        rawDataIncluded: true,
                        rawDataFieldCount: Object.keys(completeRawData).length,
                    };
                    sessionStorage.setItem('last_successful_submission', JSON.stringify(submissionRecord));
                } catch (err) {
                    console.error('[RAW_DATA/SUBMIT] ⚠️ Could not save submission record:', err);
                }
                
                // GTM Tracking - Only if GTM is enabled

                if (response.success === 1 && window.gtag && typeof window.gtag === 'function' && process.env.NEXT_PUBLIC_GTM_ID) {
                    // console.log('✅ GTM tracking enabled, proceeding...');
                    try {
                        // Hash email for privacy-compliant tracking
                        const hashedEmail = await hashEmail(payload.email);
                        
                        localStorage.setItem('FormValues', JSON.stringify(payload));
                        localStorage.setItem('JobSuccess', JSON.stringify(true));

                        // Prepare analytics data for GTM
                        if (typeof window !== "undefined" && window.gtag) {
                        const analytics = {
                            additionalInformation: payload.additionalInformation,
                            departure: payload.departure,
                            destination: payload.destination,
                            email: payload.email,
                            firstName: payload.firstName,
                            largeSuitCases: payload.largeSuitCases,
                            lastName: payload.lastName,
                            numberOfPassengers: payload.numberOfPassengers,
                            phoneNumber: payload.phoneNumber,
                            phonePreFix: payload.phonePreFix,
                            returnTime: payload.returnTime,
                            smallSuitCases: payload.smallSuitCases,
                            source: payload.source,
                            tripType: payload.tripType,
                        };

                        // Fire the Personal Information form event using existing gtmEvent
                            window.gtag('event', 'Personal_Information_form', {
                            event_category: 'Personal_Information',
                            event_label: 'Personal_Information',
                            value: analytics,
                        });
    
                        // Fire the conversion event with hashed email using gtag directly
                            window.gtag('event', 'conversion', {
                                send_to: 'AW-831601075/UNUcCNnwuuwZELPzxIwD',
                                value: 1.0,
                                currency: 'USD',
                                user_data: {
                                    email: hashedEmail,
                                },
                            });
                        }
                    } catch (trackingError) {
                        console.error('❌ GTM tracking error:', trackingError);
                        // Don't block form submission if tracking fails
                    }
                } else {
                    console.log('⚠️ GTM tracking skipped - conditions not met');
                }

                // Redirect to success page FIRST (before clearing data)
                await router.push('/booking-successful');

                // Clear booking data on successful submit (Redux + localStorage)
                // This happens AFTER navigation so the success page can access the data
                // dispatch(clearBookingForm());
                clearBookingState();
            } catch (error) {
                console.error('[RAW_DATA/SUBMIT] ❌ Error details:', {
                    message: error.message,
                    stack: error.stack,
                    name: error.name
                });

                // CRITICAL: Save failed submission data for debugging
                try {
                    const failedSubmission = {
                        timestamp: new Date().toISOString(),
                        error: {
                            message: error.message,
                            name: error.name,
                        },
                        rawDataPresent: !!bookingData.rawForm1Data,
                        rawDataFieldCount: bookingData.rawForm1Data ? Object.keys(bookingData.rawForm1Data).length : 0,
                        form2Data: {
                            email: formData.email,
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                        },
                        bookingDataKeys: Object.keys(bookingData),
                    };
                    sessionStorage.setItem('last_failed_submission', JSON.stringify(failedSubmission));
                    console.error('[RAW_DATA/SUBMIT] ❌ Failed submission data saved for debugging');
                } catch (saveError) {
                    console.error('[RAW_DATA/SUBMIT] ❌ Could not save failed submission data:', saveError);
                }

                // Provide more specific error messages
                let errorMessage = 'Failed to submit form. Please try again.';
                if (error.message.includes('Required fields are missing')) {
                    errorMessage = 'Please fill in all required fields.';
                } else if (error.message.includes('Failed to send email')) {
                    errorMessage = 'Network error. Please check your connection and try again.';
                }

                alert(errorMessage);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            console.warn('[RAW_DATA/SUBMIT] ⚠️ Form validation failed - submission blocked');
            setIsSubmitting(false);
        }
    };

    return {
        formData,
        errors,
        isClickedSubmit,
        isSubmitting,
        handleInputChange,
        validateForm,
        handleSubmit,
    };
};
